import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { formatDateRu, formatTimeRu } from "../../../utils/appointmentLabels";
import {
  sessionAccentStyle,
  groupName,
  sessionDurationMinutes,
  trainerName,
} from "../../../utils/clientSessionUtils";
import AttendancePollButtons from "./AttendancePollButtons";
import SessionStatusBadge from "./SessionStatusBadge";
import CompetitionSheetContent from "./CompetitionSheetContent";
import { clubEventTag } from "../../../utils/competitionUtils";
import { isClubEventSession } from "../../../utils/clientScheduleUtils";
import s from "./clientApp.module.css";

function sessionTimeParts(startIso, endIso) {
  return {
    start: formatTimeRu(startIso),
    end: endIso ? formatTimeRu(endIso) : null,
  };
}

function MetaRow({ icon, children }) {
  return (
    <div className={s.sheetMetaRow}>
      <span className={s.sheetMetaIcon}>{icon}</span>
      <span className={s.sheetMetaText}>{children}</span>
    </div>
  );
}

export default function SessionDetailSheet({
  appointment,
  pollRow,
  groups,
  onClose,
  onPollDone,
  onCompetitionApply,
}) {
  const [rsvpPhase, setRsvpPhase] = useState("choose");

  useEffect(() => {
    if (!appointment) return undefined;
    setRsvpPhase("choose");
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [appointment]);

  if (!appointment) return null;

  const past = new Date(appointment.start_datetime) < new Date();
  const sheetAccentStyle = sessionAccentStyle(appointment, "--sheet-accent");
  const duration = sessionDurationMinutes(
    appointment.start_datetime,
    appointment.end_datetime,
  );
  const isEvent = isClubEventSession(appointment);
  const upcoming = !past && appointment.status !== "cancelled";
  const isTraining =
    !isEvent &&
    (appointment.source === "training_session" || Boolean(appointment.session_id));
  const showRsvp = upcoming && (isTraining || pollRow) && !isEvent;
  const times = sessionTimeParts(
    appointment.start_datetime,
    appointment.end_datetime,
  );

  return createPortal(
    <div className={s.sheetBackdrop} onClick={onClose} role="presentation">
      <div
        className={`${s.sheet} ${isEvent ? s.competitionDetailSheet : ""}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={isEvent ? clubEventTag(appointment) || "Событие" : "Занятие"}
        style={isEvent ? undefined : sheetAccentStyle}
      >
        <div className={s.sheetHandle} />
        <button type="button" className={s.sheetClose} onClick={onClose} aria-label="Закрыть">
          <svg viewBox="0 0 24 24" aria-hidden>
            <path
              d="M6 6l12 12M18 6 6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {isEvent ? (
          <CompetitionSheetContent
            event={appointment}
            groups={groups}
            pollRow={pollRow}
            onApply={onCompetitionApply}
          />
        ) : (
          <>
        <div className={s.sheetHero}>
          <div className={s.sheetHeroGlow} aria-hidden />
          <span className={s.sheetHeroChip}>{groupName(appointment, groups)}</span>
          <div className={s.sheetTimeBlock}>
            <div className={s.sheetTimeRow}>
              <span className={s.sheetTimePart}>{times.start}</span>
              {times.end ? (
                <>
                  <span className={s.sheetTimeDash} aria-hidden>
                    –
                  </span>
                  <span className={s.sheetTimePart}>{times.end}</span>
                </>
              ) : null}
            </div>
            {duration ? (
              <span className={s.sheetDuration}>{duration} мин</span>
            ) : null}
          </div>
          <p className={s.sheetDate}>
            {formatDateRu(appointment.start_datetime, {
              weekday: "long",
              day: "numeric",
              month: "long",
            })}
          </p>
        </div>

        <div className={s.sheetBody}>
          <div className={s.sheetStatusRow}>
            <SessionStatusBadge appointment={appointment} pollRow={pollRow} large />
          </div>

          <div className={s.sheetMetaList}>
            <MetaRow
              icon={
                <svg viewBox="0 0 24 24" aria-hidden>
                  <path
                    d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                  />
                  <path
                    d="M4 20c0-3.3 2.7-6 6-6h4c3.3 0 6 2.7 6 6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  />
                </svg>
              }
            >
              {trainerName(appointment)}
            </MetaRow>

            {appointment.branch?.name ? (
              <MetaRow
                icon={
                  <svg viewBox="0 0 24 24" aria-hidden>
                    <path
                      d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11Z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                    />
                    <circle
                      cx="12"
                      cy="10"
                      r="2.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                    />
                  </svg>
                }
              >
                {appointment.branch.name}
                {appointment.branch.address ? (
                  <span className={s.sheetMetaSub}>{appointment.branch.address}</span>
                ) : null}
              </MetaRow>
            ) : null}
          </div>

          {showRsvp ? (
            <div
              className={`${s.sheetPollBlock} ${
                rsvpPhase === "reason" ? s.sheetPollBlockReason : ""
              } ${rsvpPhase === "answered" ? s.sheetPollBlockAnswered : ""}`}
            >
              <AttendancePollButtons
                session={appointment}
                pollRow={pollRow}
                allowDirect
                variant="sheet"
                onPhaseChange={setRsvpPhase}
                onDone={onPollDone}
              />
            </div>
          ) : null}

          {appointment.status === "cancelled" ? (
            <p className={s.sheetNote}>
              Тренировка отменена или перенесена. Следите за обновлениями в календаре.
            </p>
          ) : null}
        </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
